import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { AnimatedStatementText } from '$partials/ui/index.js';
import Image from '$partials/media/image';
gsap.registerPlugin(ScrollTrigger);

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const ModuleAnimatedStatementSmall = ({
    mainText = '',
    backgroundImageDesktop,
    backgroundImageMobile,
}) => {
    // references
    const containerRef = useRef();
    const timerRef = useRef();
    const splitRef = useRef();
    const timelineRef = useRef();

    // state
    const [ windowSize, setWindowSize ] = useState({ height: undefined, width: undefined });

    // methods
    const createAnimation = () => {
        if (containerRef.current) {
            // create timeline
            const tl = gsap.timeline({
                scrollTrigger: {
                    // markers: true,
                    start: 'top center',
                    trigger: containerRef.current
                }
            });

            timelineRef.current = tl;

            // create reveal hide of split text content
            tl.to(
                containerRef.current.querySelectorAll('.animation__mask-slide-in'),
                {
                    duration: 0.1,
                    opacity: 1,
                    overwrite: true
                },
                0
            );

            // create split text object
            const split = SplitText.create(
                containerRef.current.querySelectorAll('.animated-statement__lines'),
                {
                    linesClass: 'animation__line',
                    mask: 'lines',
                    onSplit: (self) => {
                        // on complete of split, create and add tween to timeline
                        const to = tl.to(
                            self.lines,
                            {
                                duration: 0.8,
                                ease: 'power3',
                                overwrite: true,
                                stagger: 0.15,
                                y: '0%'
                            },
                            0
                        );

                        return to;
                    },
                    type: 'lines'
                }
            );

            splitRef.current = split;

            // create scroll up tween and add to timeline
            tl.to(
                containerRef.current.querySelectorAll('.animation__scroll-up'),
                {
                    duration: 3,
                    ease: 'power3',
                    overwrite: true,
                    y: (i, target) => {
                        return (target.offsetHeight - (target.firstChild.offsetHeight)) * -1;
                    }
                },
                0
            );
        }
    };

    // hooks
    useEffect(() => {
        const handleResize = () => {
            // only recreate animation when resize width changes
            if (window.innerWidth !== windowSize.width) {
                // clear split & timeline
                splitRef?.current?.revert?.();
                timelineRef?.current?.revert?.();

                // debounce split/timeline creation
                timerRef.current && clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setWindowSize({ height: window.innerHeight, width: window.innerWidth });
                    createAnimation();
                }, 100);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => window.removeEventListener('resize', handleResize);
    }, [ windowSize.width ]);

    // render
    return (
        <section
            className='animated-statement animated-statement--small py-96 relative'
            ref={containerRef}    >
            {/* Background image */}

            {/* Content */}
            <div className='layout-container w-full pb-20 overflow-hidden'>
                <div className='px-10 sm:px-5 md:px-10 relative rounded-button overflow-hidden'>
                    {backgroundImageDesktop && (
                        <picture className={`absolute inset-0 -z-10 ${backgroundImageMobile ? 'hidden sm:block' : ''}`}>
                            <Image
                                {...backgroundImageDesktop}
                                className='w-full h-full object-cover'
                                containerClassName='w-full h-full relative'
                                height='100%'
                                loadWidth={1920}
                                width='100%' />
                        </picture>
                    )}
                    {backgroundImageMobile && (
                        <picture className='absolute inset-0 -z-10 sm:hidden'>
                            <Image
                                {...backgroundImageMobile}
                                className='w-full h-full object-cover'
                                containerClassName='w-full h-full relative'
                                height='100%'
                                loadWidth={1920}
                                width='100%' />
                        </picture>
                    )}
                    <div
                        className={`px-30 md:px-48 py-64 rounded-button ${
                            backgroundImageDesktop ? '' : 'bg-sand'
                        }`}>
                        <div className='flex flex-wrap'>
                            <div className='w-full sm:w-10/12 px-10 sm:px-5 md:px-10 m-auto text-center'>
                                <div className='type-style-1 break-words animated-statement__text'>
                                    <AnimatedStatementText>{mainText}</AnimatedStatementText>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    mainText: PropTypes.string.isRequired,
    backgroundImageDesktop: PropTypes.bool,
    backgroundImageMobile: PropTypes.bool
};

ModuleAnimatedStatementSmall.propTypes = propTypes;

export default ModuleAnimatedStatementSmall;
